import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useIsAuthenticated } from '@azure/msal-react';
import { Button, DatePicker, Form, Select, Skeleton, notification } from 'antd';
import React, { useEffect, useState } from 'react';
import { getDayJsDate } from '../../utils/datetime';
import CustomInput from '../CustomComponents/CustomInput';
import {
  ADD_NEW_APPEAL,
  GET_APPEAL,
  GET_PROCEEDINGS_QUERY,
  UPDATE_APPEAL,
  resultOptions,
  typeOptions,
} from './AppealQueries';
import { GET_PARTICIPANTS } from '../Party/PartyQueries';
import { useSearchParams } from 'react-router-dom';

interface AppealFormProps {
  onCancel: () => void;
  proceedingId: string;
  appealId: string;
  onSuccess: () => void;
}
interface OptionMap {
  value: string;
  label: string;
}

const { Option } = Select;

const AppealForm: React.FC<AppealFormProps> = ({ onCancel, proceedingId, appealId, onSuccess }) => {
  const [form] = Form.useForm();
  const today = new Date();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const [addNewAppeal, { loading: adding, error: addError, data: addData }] =
    useMutation(ADD_NEW_APPEAL);
  const [getParticipants, { data: response, loading, error, refetch }] =
    useLazyQuery(GET_PARTICIPANTS);
  const [
    getAppealDetails,
    {
      data: appealDetails,
      loading: readingAppealDetails,
      error: appealDetailsError,
      refetch: refetchAppealDetails,
    },
  ] = useLazyQuery(GET_APPEAL);
  const [updateAppeal, { loading: updating, error: updateError, data: updateData }] =
    useMutation(UPDATE_APPEAL);
  const [
    getProceedingDetails,
    {
      data: proceedingData,
      loading: proceedingLoading,
      error: proceedingError,
      refetch: refetchProceeding,
    },
  ] = useLazyQuery(GET_PROCEEDINGS_QUERY);

  const participantOptions: OptionMap[] = [];

  const isAuthenticated = useIsAuthenticated();
  const [isDirty, setIsDirty] = useState(false);
  const [showDecisionFields, setShowDecisionFields] = useState(false);
  const [isTypeLocked, setIsTypeLocked] = useState(false);

  useEffect(() => {
    if (tab === '8' && proceedingId) {
      getParticipants({
        variables: {
          input: { proceedingNumber: proceedingId },
        },
      });
    }
  }, [tab]);

  useEffect(() => {
    if (appealId) {
      getAppealDetails({
        variables: { input: { proceedingId, appealId } },
      });
    }

    getProceedingDetails({
      variables: { filter: { proceedingId } },
    });
  }, [appealId, proceedingId, getAppealDetails, getProceedingDetails]);

  useEffect(() => {
    if (appealId && appealDetails?.appeal) {
      form.setFieldsValue({ type: appealDetails.appeal.type });
      setIsTypeLocked(true);
    } else if (proceedingData?.proceedings?.data?.[0]) {
      const status = proceedingData.proceedings.data[0].status;

      if (status === 'In Progress' || status === 'Adjourned') {
        form.setFieldsValue({ type: 'Procedural' });
        setIsTypeLocked(true);
      } else if (status === 'Cancelled' || status === 'Completed') {
        form.setFieldsValue({ type: 'Hearing' });
        setIsTypeLocked(true);
      }
    }
  }, [appealId, appealDetails, proceedingData, form]);

  useEffect(() => {
    if (appealDetails?.appeal) {
      form.setFieldsValue({
        ...appealDetails.appeal,
        requestDate: getDayJsDate(appealDetails.appeal.requestDate),
        resultDate: getDayJsDate(appealDetails.appeal.resultDate),
        decisionDate: getDayJsDate(appealDetails.appeal.decisionDate),
        participantId: {
          value: appealDetails.appeal.participantId,
          label: participantOptions.find(
            (option) => option.value === appealDetails.appeal.participantId
          )?.label,
        },
      });
    }
  }, [appealDetails, form]);

  response?.participants?.data.map((participant: any) => {
    participantOptions.push({
      value: participant.id,
      label: `${participant.partyName}`,
    });
  });

  const onFinish = async (values: any) => {
    try {
      const input = {
        id: appealId,
        proceedingId,
        ...values,
        participantId: values.participantId?.value,
        requestDate: values.requestDate,
        resultDate: values.resultDate,
        decisionDate: values.decisionDate,
      };

      if (appealId) {
        const { data, errors } = await updateAppeal({ variables: { input } });

        if (errors || data.updateAppeal.result !== 'Success') {
          notification.error({
            message: 'Error',
            description: errors?.[0]?.message || 'Failed to update appeal',
          });
          return;
        }

        notification.success({
          message: 'Success',
          description: 'Appeal updated successfully',
        });
      } else {
        const { data, errors } = await addNewAppeal({ variables: { input } });

        if (errors || data.addAppeal.result !== 'Success') {
          notification.error({
            message: 'Error',
            description: errors?.[0]?.message || 'Failed to add appeal',
          });
          return;
        }

        notification.success({
          message: 'Success',
          description: 'Appeal added successfully',
        });
      }

      onSuccess();
      onCancel();
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: 'Error in saving appeal',
      });
    }
  };

  return (
    <>
      {(readingAppealDetails || updating || proceedingLoading) && <Skeleton />}
      {!(readingAppealDetails || updating || proceedingLoading) && (
        <div className="thin-border">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onValuesChange={() => setIsDirty(true)}
          >
            <Form.Item
              name="type"
              label="Type of Appeal"
              rules={[{ required: true, message: 'Please select the type' }]}
            >
              <Select
                options={typeOptions}
                disabled={!isAuthenticated || isTypeLocked}
                data-testid="type-select"
              />
            </Form.Item>

            <Form.Item
              name="participantId"
              label="Party Name"
              rules={[{ required: true, message: 'Please select an option' }]}
            >
              <Select labelInValue disabled={!isAuthenticated} data-testid="participant-select">
                {participantOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="requestDate"
              label="Request Date"
              rules={[{ required: true, message: 'Please select the request date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabled={!isAuthenticated}
                disabledDate={(current) => current.isAfter(today)}
                data-testid="request-date-picker"
              />
            </Form.Item>

            <Form.Item
              name="resultDate"
              label="Result Date"
              rules={[{ required: true, message: 'Please select the result date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabled={!isAuthenticated}
                disabledDate={(current) => current.isAfter(today)}
                data-testid="result-date-picker"
              />
            </Form.Item>

            <Form.Item
              name="result"
              label="Result of the Appeal"
              rules={[{ required: true, message: 'Please select the result' }]}
            >
              <Select
                options={resultOptions}
                disabled={!isAuthenticated}
                data-testid="result-select"
              />
            </Form.Item>

            <Form.Item name="decision" label="Decision of the Appeal">
              <CustomInput showInput={true} disabled={!isAuthenticated} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="decisionDate"
              label="Decision Date"
              rules={[{ required: true, message: 'Please select the decision date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabled={!isAuthenticated}
                disabledDate={(current) => current.isAfter(today)}
                data-testid="decision-date-picker"
              />
            </Form.Item>

            <Form.Item name="comments" label="Comments">
              <CustomInput
                showInput={true}
                disabled={!isAuthenticated}
                maxLength={80}
                style={{ width: '100%' }}
                data-testid="comments-textarea"
              />
            </Form.Item>

            {isAuthenticated && (
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!isDirty}
                  loading={adding || updating}
                >
                  Submit
                </Button>
                <Button onClick={onCancel} style={{ marginLeft: 8 }} data-testid="cancel-button">
                  Cancel
                </Button>
              </Form.Item>
            )}
            {!isAuthenticated && (
              <Button
                type="primary"
                onClick={onCancel}
                style={{ marginLeft: 8 }}
                data-testid="back-button"
              >
                Back
              </Button>
            )}
          </Form>
        </div>
      )}
    </>
  );
};

export default AppealForm;
